// Akte: „Für alle im Haushalt / im Verbund generieren" für die Kundenerst-
// information. Lädt das Umfeld des Kontakts und bietet nur an, was es gibt.
// Haushalt: Personen. Verbund: bei einer Firma die Verbundfirmen, bei einer
// Person ihre Unternehmen („Verbindung zu Unternehmen" — die Akte kennt sie
// schon und reicht sie herein). Ablauf und Rechte: siehe `erstinfoSammel.ts`.

import * as React from "react";
import { fetchHaushalt, fetchUnternehmensverbund } from "../redesign/table/useUnternehmensverbund";
import type { Haushalt } from "../redesign/table/haushalt";
import type { Unternehmensverbund } from "../redesign/table/unternehmensverbund";
import { erstinfoJeKontakt, mitgliederAusUmfeld, sammelMeldung, type SammelArt } from "./erstinfoSammel";

export type ErstinfoSammelOption = { art: SammelArt; anzahl: number };

export type ErstinfoSammelOptionen = {
  /** false = nichts laden (z. B. wenn die Erstinformation gar nicht angezeigt wird). */
  aktiv?: boolean;
  /** Anzeigename des Kontakts selbst (für Dateiname und Meldung). */
  name?: string;
  /** Bei einer Person: ihre Firmen aus „Verbindung zu Unternehmen". */
  unternehmen?: Array<{ id: number; name: string }>;
};

export function useErstinfoSammel(
  contactId: number,
  onMeldung: (text: string) => void,
  { aktiv = true, name, unternehmen = [] }: ErstinfoSammelOptionen = {},
) {
  const [umfeld, setUmfeld] = React.useState<{ haushalt: Haushalt | null; verbund: Unternehmensverbund | null } | null>(
    null,
  );
  const [laeuft, setLaeuft] = React.useState<SammelArt | null>(null);

  React.useEffect(() => {
    setUmfeld(null);
    if (!aktiv) return;
    const ctrl = new AbortController();
    Promise.all([
      fetchHaushalt(contactId, ctrl.signal).catch(() => null),
      fetchUnternehmensverbund(contactId, ctrl.signal).catch(() => null),
    ]).then(([haushalt, verbund]) => {
      if (!ctrl.signal.aborted) setUmfeld({ haushalt, verbund });
    });
    return () => ctrl.abort();
  }, [contactId, aktiv]);

  const mitglieder = (art: SammelArt) =>
    umfeld ? mitgliederAusUmfeld(contactId, art, umfeld.haushalt, umfeld.verbund, unternehmen) : [];

  const optionen: ErstinfoSammelOption[] = umfeld
    ? (["haushalt", "verbund"] as const).map((art) => ({ art, anzahl: mitglieder(art).length })).filter((o) => o.anzahl > 0)
    : [];

  const selbstName =
    name?.trim() || umfeld?.haushalt?.person?.name?.trim() || umfeld?.verbund?.firma?.name?.trim() || `Kontakt ${contactId}`;

  const starten = async (art: SammelArt): Promise<void> => {
    if (!umfeld || laeuft) return;
    setLaeuft(art);
    try {
      const ziele = [{ contactId, name: selbstName }, ...mitglieder(art)];
      onMeldung(sammelMeldung(await erstinfoJeKontakt(ziele)));
    } catch (err) {
      onMeldung(err instanceof Error ? err.message : "Erzeugen fehlgeschlagen.");
    } finally {
      setLaeuft(null);
    }
  };

  return { optionen, starten, laeuft };
}

/** „Für alle im Haushalt generieren (3)" — mitgezählt ist der Kontakt selbst. */
export const sammelOptionText = (o: ErstinfoSammelOption, label: string): string => `${label} (${o.anzahl + 1})`;
